import { addDays, mondayOf } from './checkin-weeks';
import { participantChannels, StageRole, TrackerConfig, TrackerFunnel } from './tracker-config';

export type TrackerCloseAs = 'booked' | 'collected';
export type TrackerTouchKind = 'step' | 'payment' | 'note';

export type TrackerTouchInput = {
  personId: string;
  kind: TrackerTouchKind;
  stageKey?: string;
  /** YYYY-MM-DD. */
  happenedOn: string;
  createdAt: Date | string;
  amountCents?: number;
};

export type TrackerParticipantInput = {
  closeAs?: TrackerCloseAs;
  targetCents?: number;
  /** Leads: the step that makes a person a lead, e.g. "Booked call". */
  destination?: string;
  /** Leads: hours of spend are priced at this rate, the current one. */
  hourlyRateCents?: number;
  /** Leads: the participant's own allowable cost per lead. */
  cplTargetCents?: number;
  /** Leads: the participant's own channels, each ending at the destination. */
  funnels?: TrackerFunnel[];
};

/** One spend entry: dollars or minutes, never both. */
export type TrackerSpendEntryInput = {
  /** YYYY-MM-DD. */
  spentOn: string;
  /** One of the participant's channels; undefined = all channels. */
  channelKey?: string;
  dollarsCents?: number;
  minutes?: number;
};

/** A participant's spend and the rate its hours are priced at. */
export type TrackerSpendInput = {
  entries: TrackerSpendEntryInput[];
  hourlyRateCents?: number;
};

/** An entry in cents: dollars as spent, hours at the current rate. */
export function spendEntryCents(entry: TrackerSpendEntryInput, hourlyRateCents = 0) {
  if (entry.dollarsCents !== undefined) {
    return entry.dollarsCents;
  }
  return Math.round(((entry.minutes ?? 0) * hourlyRateCents) / 60);
}

/** Total spend in cents over the entries kept by `keep`. */
export function spendCentsOf(
  spend: TrackerSpendInput,
  keep: (entry: TrackerSpendEntryInput) => boolean = () => true,
) {
  return spend.entries
    .filter(keep)
    .reduce((sum, e) => sum + spendEntryCents(e, spend.hourlyRateCents), 0);
}

export type TrackerRollup = {
  sends: number;
  replies: number;
  booked: number;
  held: number;
  closes: number;
  bookedCents: number;
  collectedCents: number;
  byStage: Record<string, number>;
  /** Step touches of any stage: "Steps logged" on a Leads course. */
  steps: number;
  /** People whose first step at a destination falls in the range: one lead per person. */
  leads: number;
  /** Spend in the range, when the caller passed the participant's spend. */
  spendCents?: number;
};

export type TrackerWeekRollup = TrackerRollup & { weekStart: string };

export type TrackerScoreboardResult =
  | {
      kind: 'revenue';
      closeAs?: TrackerCloseAs;
      targetCents?: number;
      progressCents: number;
      bookedCents: number;
      collectedCents: number;
    }
  | {
      kind: 'cpl';
      /** Dollars plus hours at the participant's current rate. */
      spendCents: number;
      dollarsCents: number;
      minutes: number;
      leads: number;
      cplCents?: number;
      /** The participant's own target; undefined until they set it. */
      targetCents?: number;
    };

/**
 * Each person's first touch matching `keep`, by date then entry order. One
 * close per person: a second Won or a later payment does not count again.
 */
export function firstTouchPerPerson<T extends TrackerTouchInput>(
  touches: T[],
  keep: (t: T) => boolean,
): T[] {
  const first = new Map<string, T>();
  for (const touch of touches.filter(keep).sort(compareTouches)) {
    if (!first.has(touch.personId)) {
      first.set(touch.personId, touch);
    }
  }
  return [...first.values()];
}

function roleByKey(config: TrackerConfig) {
  const roles = new Map<string, StageRole | undefined>();
  for (const channel of config.channels) {
    for (const stage of [...channel.stages, ...channel.exits]) {
      roles.set(stage.key, stage.role);
    }
  }
  return roles;
}

function isWon(roles: Map<string, StageRole | undefined>, touch: TrackerTouchInput) {
  return touch.kind === 'step' && !!touch.stageKey && roles.get(touch.stageKey) === 'won';
}

function isLead(roles: Map<string, StageRole | undefined>, touch: TrackerTouchInput) {
  return touch.kind === 'step' && !!touch.stageKey && roles.get(touch.stageKey) === 'lead';
}

/**
 * Each lead's first step at a destination: a person counts once, on the
 * day they first reached it, whichever of the participant's channels.
 */
export function leadTouches<T extends TrackerTouchInput>(config: TrackerConfig, touches: T[]) {
  const roles = roleByKey(config);
  return firstTouchPerPerson(touches, (t) => isLead(roles, t));
}

function createdMs(touch: TrackerTouchInput) {
  return new Date(touch.createdAt).getTime();
}

function compareTouches(a: TrackerTouchInput, b: TrackerTouchInput) {
  if (a.happenedOn !== b.happenedOn) {
    return a.happenedOn < b.happenedOn ? -1 : 1;
  }
  return createdMs(a) - createdMs(b);
}

/**
 * A person's stage: the latest step touch when it is a side exit, otherwise
 * the furthest ladder stage any step touch reached, so a later send never
 * moves someone back down the ladder. With no step logged, a sequence
 * channel's waiting stage ("To contact"); a steps channel has none, since
 * every stage there is an action, so the person is not started (undefined).
 */
export function deriveStage(
  config: TrackerConfig,
  channelKey: string,
  touches: TrackerTouchInput[],
): string | undefined {
  const channel = config.channels.find((c) => c.key === channelKey);
  if (!channel) {
    return;
  }
  const ladder = channel.stages.map((s) => s.key);
  const exits = new Set(channel.exits.map((s) => s.key));
  const steps = touches
    .filter((t) => t.kind === 'step' && t.stageKey)
    .filter((t) => ladder.includes(t.stageKey!) || exits.has(t.stageKey!))
    .sort(compareTouches);
  if (!steps.length) {
    const first = channel.stages[0];
    return channel.flow === 'sequence' && first && !first.role ? first.key : undefined;
  }
  const latest = steps[steps.length - 1];
  if (exits.has(latest.stageKey!)) {
    return latest.stageKey;
  }
  const furthest = Math.max(...steps.map((t) => ladder.indexOf(t.stageKey!)));
  return ladder[furthest];
}

/**
 * Totals over an inclusive date range by happened_on. `touches` should hold
 * every touch of the people counted, not only the range's: a collected close
 * is a person's first payment ever, which may sit before the range.
 */
export function rollupRange(
  config: TrackerConfig,
  touches: TrackerTouchInput[],
  closeAs: TrackerCloseAs | undefined,
  start: string,
  end: string,
  spend?: TrackerSpendInput,
): TrackerRollup {
  const roles = roleByKey(config);
  const inRange = (t: TrackerTouchInput) => t.happenedOn >= start && t.happenedOn <= end;
  const result: TrackerRollup = {
    sends: 0,
    replies: 0,
    booked: 0,
    held: 0,
    closes: 0,
    bookedCents: 0,
    collectedCents: 0,
    byStage: {},
    steps: 0,
    leads: 0,
  };

  for (const touch of touches) {
    if (!inRange(touch)) {
      continue;
    }
    if (touch.kind === 'payment') {
      result.collectedCents += touch.amountCents ?? 0;
      continue;
    }
    if (touch.kind !== 'step' || !touch.stageKey) {
      continue;
    }
    result.byStage[touch.stageKey] = (result.byStage[touch.stageKey] ?? 0) + 1;
    result.steps++;
    const role = roles.get(touch.stageKey);
    if (role === 'contacted') {
      result.sends++;
    } else if (role === 'replied' || role === 'replied_no') {
      result.replies++;
    } else if (role === 'booked') {
      result.booked++;
    } else if (role === 'held') {
      result.held++;
    }
  }

  if (participantChannels(config)) {
    result.leads = firstTouchPerPerson(touches, (t) => isLead(roles, t)).filter(inRange).length;
  }
  if (spend) {
    result.spendCents = spendCentsOf(spend, (e) => e.spentOn >= start && e.spentOn <= end);
  }
  const wins = firstTouchPerPerson(touches, (t) => isWon(roles, t)).filter(inRange);
  result.bookedCents = wins.reduce((sum, t) => sum + (t.amountCents ?? 0), 0);
  if (closeAs === 'booked') {
    result.closes = wins.length;
  } else if (closeAs === 'collected') {
    result.closes = firstTouchPerPerson(touches, (t) => t.kind === 'payment').filter(
      inRange,
    ).length;
  }

  return result;
}

/** One week's totals, Monday to Sunday by happened_on. */
export function rollupWeek(
  config: TrackerConfig,
  touches: TrackerTouchInput[],
  closeAs: TrackerCloseAs | undefined,
  weekStart: string,
  spend?: TrackerSpendInput,
): TrackerWeekRollup {
  const start = mondayOf(weekStart);
  return {
    weekStart: start,
    ...rollupRange(config, touches, closeAs, start, addDays(start, 6), spend),
  };
}

/**
 * Spend ÷ leads, rounded to the cent. Undefined with no leads, and with no
 * spend: cost per lead waits for spend, so "$0 per lead" never reads as a
 * win. Every surface (scoreboard, trend, by channel, coach, ACES) uses this.
 */
export function cplOf(spendCents: number | undefined, leads: number) {
  return leads && spendCents ? Math.round(spendCents / leads) : undefined;
}

/** The participant's scoreboard across the whole immersion sprint. */
export function scoreboard(
  config: TrackerConfig,
  touches: TrackerTouchInput[],
  participant: TrackerParticipantInput,
  spendEntries: TrackerSpendEntryInput[] = [],
): TrackerScoreboardResult {
  if (config.scoreboard.kind === 'cpl') {
    const leads = leadTouches(config, touches).length;
    const spend = { entries: spendEntries, hourlyRateCents: participant.hourlyRateCents };
    const spendCents = spendCentsOf(spend);
    return {
      kind: 'cpl',
      spendCents,
      dollarsCents: spendEntries.reduce((sum, e) => sum + (e.dollarsCents ?? 0), 0),
      minutes: spendEntries.reduce((sum, e) => sum + (e.minutes ?? 0), 0),
      leads,
      cplCents: cplOf(spendCents, leads),
      targetCents: participant.cplTargetCents,
    };
  }

  const roles = roleByKey(config);
  let bookedCents = 0;
  let collectedCents = 0;
  for (const touch of touches) {
    if (touch.kind === 'payment') {
      collectedCents += touch.amountCents ?? 0;
    }
  }
  for (const touch of firstTouchPerPerson(touches, (t) => isWon(roles, t))) {
    bookedCents += touch.amountCents ?? 0;
  }
  const progressCents =
    participant.closeAs === 'booked'
      ? bookedCents
      : participant.closeAs === 'collected'
        ? collectedCents
        : 0;
  return {
    kind: 'revenue',
    closeAs: participant.closeAs,
    targetCents: participant.targetCents,
    progressCents,
    bookedCents,
    collectedCents,
  };
}

export type TrackerScoreboardDetails = {
  peopleContacted: number;
  replies: number;
  interested: number;
  /** Replies ÷ people contacted, as a percentage to one decimal place. */
  replyRate?: number;
  held: number;
  decisions: number;
  wins: number;
  bookedCents: number;
  collectedCents: number;
  /** Target less progress on the participant's own close choice; undefined without both. */
  gapCents?: number;
};

/**
 * The sheet's Scoreboard tab figures, all-time: what the Field Guide asks
 * coaches to read before every call.
 */
export function scoreboardDetails(
  config: TrackerConfig,
  touches: TrackerTouchInput[],
  participant: TrackerParticipantInput,
): TrackerScoreboardDetails {
  const roles = roleByKey(config);
  const people = (role: StageRole[]) =>
    new Set(
      touches
        .filter((t) => t.kind === 'step' && t.stageKey && role.includes(roles.get(t.stageKey)!))
        .map((t) => t.personId),
    );
  const count = (role: StageRole[]) =>
    touches.filter((t) => t.kind === 'step' && t.stageKey && role.includes(roles.get(t.stageKey)!))
      .length;
  const contacted = people(['contacted']).size;
  const replies = count(['replied', 'replied_no']);
  const board = scoreboard(config, touches, participant);
  const bookedCents = board.kind === 'revenue' ? board.bookedCents : 0;
  const collectedCents = board.kind === 'revenue' ? board.collectedCents : 0;
  const gapCents =
    board.kind === 'revenue' && participant.closeAs && participant.targetCents !== undefined
      ? Math.max(0, participant.targetCents - board.progressCents)
      : undefined;
  return {
    peopleContacted: contacted,
    replies,
    interested: count(['replied']),
    replyRate: contacted ? Math.round((replies / contacted) * 1000) / 10 : undefined,
    held: count(['held']),
    decisions: people(['won']).size + people(['lost']).size,
    wins: people(['won']).size,
    bookedCents,
    collectedCents,
    gapCents,
  };
}
